import { Fragment, ReactNode, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationIcon } from '@heroicons/react/outline'
import { DatePickerWithError } from 'components/FormInputs/DatePicker'
import { getFirstDayOfMonth, getLastDayOfMonth } from 'utils/datetime'
import { DatePickerAttributes } from './context'

interface ConfirmationDialogProps {
  open: boolean
  title: ReactNode
  message: ReactNode
  onConfirm: () => any
  onCancel?: () => any
  confirmButtonLabel?: ReactNode
  datePicker?: boolean
  datePickerAttributes?: DatePickerAttributes
  date?: Date
  onDateChange?: (value: any) => any
}

const handleClose = () => {
  // Do nothing
}

export function ConfirmationDialog({
  open,
  title,
  message,
  onConfirm,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCancel = () => {},
  confirmButtonLabel = 'Confirmar',
  datePicker = false,
  datePickerAttributes = {
    minDate: getFirstDayOfMonth(),
    maxDate: getLastDayOfMonth()
  },
  date = new Date(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDateChange = (_value: any) => {}
}: ConfirmationDialogProps) {
  const cancelButtonRef = useRef(null)

  const [isDatePickerVisible, setDatePickerVisible] = useState(false)

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={`inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 ${
                isDatePickerVisible ? 'h-auto' : ''
              }`}
            >
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon
                    className="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {title}
                  </Dialog.Title>
                  {datePicker && (
                    <div className="w-2/3 mt-3 mb-3">
                      <DatePickerWithError
                        label="Data"
                        name="date"
                        date={date}
                        minDate={datePickerAttributes.minDate}
                        maxDate={datePickerAttributes.maxDate}
                        filterDate={datePickerAttributes.filterDate}
                        onChange={(value) => {
                          onDateChange(value)
                          setDatePickerVisible(true)
                        }}
                      />
                    </div>
                  )}
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{message}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onConfirm}
                >
                  {confirmButtonLabel}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onCancel}
                  ref={cancelButtonRef}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
